'use client';

import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, type UIEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { getVoiceDetail, listVoices, updateVoice } from '@/app/apis';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineSwap } from '@/app/components/icons/BaseLineSwap';
import { Loading } from '@/app/components/icons/Loading';
import { AudioPreviewCard } from '@/app/components/shared/AudioPreviewCard';
import { AudioSelectionDialog } from '@/app/components/shared/AudioSelectionDialog';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import { VoiceInfoEditDialog } from '@/app/components/shared/VoiceInfoEditDialog';
import { VoiceOptionItem } from '@/app/components/shared/VoiceOptionItem';
import { Button } from '@/app/components/ui/button';
import { CheckboxFill } from '@/app/components/ui/checkbox-fill';
import { DialogTitle } from '@/app/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { toast } from '@/app/components/ui/use-toast';
import {
  DEFAULT_TTS_VOICE_ID,
  IMG_CUSTOM_VOICE_DEFAULT,
  IMG_DEFAULT,
  SEARCH_EMPTY_ICON,
  SITE,
  TTS_VOICE_LANGUAGES,
  TTS_VOICE_LIST,
  TTS_VOICE_LIST_BY_LANGUAGE,
  TTS_VOICE_MAP,
  type TTSVoiceLanguage,
  type TTSVoiceOption,
} from '@/app/constants';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { AudioOption } from '@/app/types';
import { cn } from '@/app/utils';
import { isVoiceNameExistsError } from '@/app/utils/client';
import { shouldFetchSelectedCustomVoice } from './voice-selection-dialog-utils';

const CUSTOM_VOICES_PAGE_SIZE = 20;
const CUSTOM_VOICES_LOAD_MORE_THRESHOLD = 100;

/** 与主体（元素）表单「无配音」选项一致的空 id */
export const ELEMENT_NO_VOICE_ID = '';

type VoiceDialogTab = 'library' | 'custom';

/** 用户自定义音色在弹窗内的展示形态（库内条目仍用 {@link TTSVoiceOption}） */
export interface VoiceSelectionCustomOption extends Omit<TTSVoiceOption, 'lang' | 'blurHash'> {
  lang?: TTSVoiceLanguage;
  blurHash?: string;
  description?: string;
  is_my: true;
}

const EMPTY_CUSTOM_VOICES: VoiceSelectionCustomOption[] = [];

interface CustomVoicesPaginationParams {
  page: number;
  pageToken?: string;
}

function mapCustomVoices(
  voices: NonNullable<Awaited<ReturnType<typeof listVoices>>['data']>['voices'],
): VoiceSelectionCustomOption[] {
  return (voices ?? [])
    .map((voice) => ({
      id: voice.voice_id ?? '',
      name: voice.name ?? '',
      description: voice.description ?? '',
      url: voice.sample_uri ?? '',
      img: IMG_CUSTOM_VOICE_DEFAULT,
      is_my: true as const,
    }))
    .filter((voice) => voice.id);
}

function appendCustomVoices(
  currentVoices: VoiceSelectionCustomOption[],
  nextVoices: VoiceSelectionCustomOption[],
): VoiceSelectionCustomOption[] {
  const voiceMap = new Map(currentVoices.map((voice) => [voice.id, voice]));

  for (const voice of nextVoices) {
    if (!voiceMap.has(voice.id)) {
      voiceMap.set(voice.id, voice);
    }
  }

  return Array.from(voiceMap.values());
}

interface VoiceSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** undefined uses the scene's default voice; null means no library voice is selected. */
  value?: string | null;
  onConfirm: (voiceId: string, voice: TTSVoiceOption | VoiceSelectionCustomOption | null) => void;
  /** 为 true 时允许选择「无配音」（voiceId 为 {@link ELEMENT_NO_VOICE_ID}，`onConfirm` 第二参为 `null`） */
  allowNoVoice?: boolean;
  /** `allowNoVoice` 为 true 时展示的无配音文案 */
  noSoundLabel?: string;
  /** 传入时替代默认的 `AudioPreviewCard` 触发器 */
  trigger?: ReactNode;
  /** `VoiceInfoEditDialog` 内表单 id 后缀，避免同页多个弹窗冲突 */
  voiceInfoEditFieldSuffix?: string;
  /** 仅参考音频入口传入；其他音色选择场景不展示上传按钮。 */
  onUploadAudio?: () => void;
  /** 当外部使用自定义 trigger 时，同步当前 voice_id 解析出的名称。 */
  onResolvedVoice?: (voice: TTSVoiceOption | VoiceSelectionCustomOption) => void;
}

export const VoiceSelectionDialog: FC<VoiceSelectionDialogProps> = ({
  open,
  onOpenChange,
  title,
  value,
  onConfirm,
  allowNoVoice = false,
  noSoundLabel,
  trigger: triggerFromProps,
  voiceInfoEditFieldSuffix = 'tts',
  onResolvedVoice,
  onUploadAudio,
}) => {
  const t = useTranslations();
  const { userInfo } = useStore((state) => ({ userInfo: state.userInfo }));
  const defaultVoiceId = allowNoVoice ? ELEMENT_NO_VOICE_ID : DEFAULT_TTS_VOICE_ID;
  const selectedVoiceId = value === null ? undefined : (value ?? defaultVoiceId);
  const [draftVoiceId, setDraftVoiceId] = useState(selectedVoiceId);
  const [language, setLanguage] = useState<TTSVoiceLanguage>('all');
  const [activeTab, setActiveTab] = useState<VoiceDialogTab>('library');
  const [customVoiceDrafts, setCustomVoiceDrafts] = useState<VoiceSelectionCustomOption[]>(EMPTY_CUSTOM_VOICES);
  const [editingVoiceId, setEditingVoiceId] = useState<string>();
  const [editingName, setEditingName] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [isSavingVoiceInfo, setIsSavingVoiceInfo] = useState(false);
  const [customVoicePage, setCustomVoicePage] = useState(0);
  const [nextCustomVoicePageToken, setNextCustomVoicePageToken] = useState<string>();
  const [hasMoreCustomVoices, setHasMoreCustomVoices] = useState(false);

  const { loading: isFetchingCustomVoices, run: fetchCustomVoices } = useFetch(
    ({ page, pageToken }: CustomVoicesPaginationParams) =>
      listVoices({
        query: {
          'pager.pagesz': CUSTOM_VOICES_PAGE_SIZE,
          ...(pageToken ? { 'pager.page_token': pageToken } : { 'pager.page': page }),
        },
      }),
    {
      manual: true,
      suppressUnauthorizedLoginDialog: true,
      onSuccess: (result, params) => {
        const [pagination] = params;
        const nextCustomVoices = mapCustomVoices(result.data?.voices);
        const nextPageToken = result.data?.next_page_token;

        setCustomVoiceDrafts((voices) =>
          pagination.page === 0 ? nextCustomVoices : appendCustomVoices(voices, nextCustomVoices),
        );
        setCustomVoicePage(pagination.page);
        setNextCustomVoicePageToken(nextPageToken);
        setHasMoreCustomVoices(Boolean(nextPageToken) || nextCustomVoices.length >= CUSTOM_VOICES_PAGE_SIZE);
      },
    },
  );

  useEffect(() => {
    if (open && userInfo?.id) {
      setCustomVoiceDrafts(EMPTY_CUSTOM_VOICES);
      setCustomVoicePage(0);
      setNextCustomVoicePageToken(undefined);
      setHasMoreCustomVoices(false);
      fetchCustomVoices({ page: 0 });
    }
  }, [fetchCustomVoices, open, userInfo?.id]);

  const customVoiceMap = useMemo(
    () => Object.fromEntries(customVoiceDrafts.map((voice) => [voice.id, voice])),
    [customVoiceDrafts],
  );

  const { run: fetchSelectedCustomVoice } = useFetch(
    (voiceId: string) =>
      getVoiceDetail({
        path: {
          voice_id: voiceId,
        },
      }),
    {
      manual: true,
      suppressUnauthorizedLoginDialog: true,
      onSuccess: (result) => {
        const [voice] = mapCustomVoices(result.data ? [result.data] : []);
        if (voice) {
          setCustomVoiceDrafts((voices) => appendCustomVoices(voices, [voice]));
        }
      },
    },
  );

  useEffect(() => {
    if (
      userInfo?.id &&
      selectedVoiceId &&
      shouldFetchSelectedCustomVoice({
        selectedVoiceId,
        customVoiceMap,
        libraryVoiceMap: TTS_VOICE_MAP,
      })
    ) {
      fetchSelectedCustomVoice(selectedVoiceId);
    }
  }, [customVoiceMap, fetchSelectedCustomVoice, selectedVoiceId, userInfo?.id]);

  const selectedAudio = useMemo<TTSVoiceOption | VoiceSelectionCustomOption | undefined>(() => {
    if (!selectedVoiceId) {
      return undefined;
    }

    return TTS_VOICE_MAP[selectedVoiceId] ?? customVoiceMap[selectedVoiceId];
  }, [customVoiceMap, selectedVoiceId]);

  useEffect(() => {
    if (selectedAudio) {
      onResolvedVoice?.(selectedAudio);
    }
  }, [onResolvedVoice, selectedAudio]);

  const filteredVoiceList = useMemo(() => {
    return TTS_VOICE_LIST_BY_LANGUAGE[language] ?? TTS_VOICE_LIST;
  }, [language]);

  const languageList = useMemo(
    () =>
      TTS_VOICE_LANGUAGES.map((item) => ({
        id: item,
        name: t(`tools.textToSpeech.languages.${item}`),
      })),
    [t],
  );

  const voiceTriggerAudio = useMemo<AudioOption>(() => {
    if (selectedAudio) {
      return { ...selectedAudio, img: selectedAudio.img || IMG_DEFAULT };
    }

    return {
      id: allowNoVoice ? ELEMENT_NO_VOICE_ID : '',
      img: IMG_DEFAULT,
      name: title,
      description: t('elements.elementDialog.soundPlaceholder'),
    };
  }, [allowNoVoice, selectedAudio, t, title]);

  const handleDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (nextOpen) {
        setDraftVoiceId(selectedVoiceId);
        setActiveTab(
          selectedVoiceId && selectedVoiceId !== ELEMENT_NO_VOICE_ID && customVoiceMap[selectedVoiceId]
            ? 'custom'
            : 'library',
        );
        setLanguage(selectedAudio?.lang ?? (SITE === 'china' ? 'zh' : 'en'));
      }
    },
    [customVoiceMap, onOpenChange, selectedAudio?.lang, selectedVoiceId],
  );

  const handleConfirm = useCallback(() => {
    if (allowNoVoice && draftVoiceId === ELEMENT_NO_VOICE_ID) {
      onConfirm(ELEMENT_NO_VOICE_ID, null);
      onOpenChange(false);
      return;
    }

    if (!draftVoiceId) return;
    const nextVoice = TTS_VOICE_MAP[draftVoiceId] ?? customVoiceMap[draftVoiceId];
    if (!nextVoice) {
      return;
    }

    onConfirm(draftVoiceId, nextVoice);
    onOpenChange(false);
  }, [allowNoVoice, customVoiceMap, draftVoiceId, onConfirm, onOpenChange]);

  const handleEditCustomVoice = useCallback(
    (voiceId: string) => {
      const voice = customVoiceMap[voiceId];
      if (!voice) {
        return;
      }

      setEditingVoiceId(voiceId);
      setEditingName(voice.name);
      setEditingDescription(voice.description ?? '');
    },
    [customVoiceMap],
  );

  const handleSaveCustomVoice = useCallback(async () => {
    if (!editingVoiceId) {
      return;
    }

    const currentVoice = customVoiceMap[editingVoiceId];
    if (!currentVoice) {
      return;
    }

    if (currentVoice.name === editingName && (currentVoice.description ?? '') === editingDescription) {
      setEditingVoiceId(undefined);
      return;
    }

    setIsSavingVoiceInfo(true);
    try {
      const result = await updateVoice({
        path: {
          'voice.voice_id': editingVoiceId,
        },
        body: {
          mask: 'name,description',
          voice: {
            voice_id: editingVoiceId,
            name: editingName,
            description: editingDescription,
          },
        },
      });
      if (result.error) {
        throw result.error;
      }
    } catch (error: unknown) {
      toast({
        type: 'error',
        message: isVoiceNameExistsError(error)
          ? t('tools.voiceClone.validation.nameDuplicate')
          : t('agentProject.operationSelect.error'),
      });
      setIsSavingVoiceInfo(false);
      return;
    }

    setCustomVoiceDrafts((voices) =>
      voices.map((voice) =>
        voice.id === editingVoiceId ? { ...voice, name: editingName, description: editingDescription } : voice,
      ),
    );
    setEditingVoiceId(undefined);
    setIsSavingVoiceInfo(false);
  }, [customVoiceMap, editingDescription, editingName, editingVoiceId, t]);

  const handleLoadMoreCustomVoices = useCallback(() => {
    if (isFetchingCustomVoices || !hasMoreCustomVoices) {
      return;
    }

    fetchCustomVoices({
      page: customVoicePage + 1,
      pageToken: nextCustomVoicePageToken,
    });
  }, [customVoicePage, fetchCustomVoices, hasMoreCustomVoices, isFetchingCustomVoices, nextCustomVoicePageToken]);

  const handleCustomVoicesScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

      if (distanceToBottom > CUSTOM_VOICES_LOAD_MORE_THRESHOLD) {
        return;
      }

      handleLoadMoreCustomVoices();
    },
    [handleLoadMoreCustomVoices],
  );

  return (
    <>
      <AudioSelectionDialog
        open={open}
        onOpenChange={handleDialogOpenChange}
        triggerAsChild
        contentClassName="h-[500px] w-[800px] border-system-white04 bg-[#28292a] p-0 sm:h-[min(560px,calc(100dvh-32px))] sm:w-[calc(100vw-32px)] sm:max-w-[calc(100vw-32px)]"
        bodyClassName="border-system-white08 border-t bg-system-white01"
        footerClassName={cn(
          'border-system-white08 border-t bg-system-white01 sm:h-auto sm:px-4 sm:py-3 sm:[&>button]:flex-1 sm:[&>button]:px-4',
          onUploadAudio && 'sm:flex-wrap sm:gap-2 sm:space-x-0',
        )}
        closeClassName="top-6 right-4 text-system-white/72 hover:text-system-white sm:top-5"
        disableOverlayClose={!!editingVoiceId}
        header={
          <DialogTitle className="hide-scrollbar flex items-center gap-4 overflow-x-auto p-4 pr-20 font-medium text-base text-system-white leading-[1.4] sm:gap-2 sm:pr-12">
            <button
              type="button"
              className={cn(
                'shrink-0 rounded-8 px-4 py-2 transition-colors sm:px-3',
                activeTab === 'library' ? 'border border-system-white16 bg-system-white08' : 'bg-system-white04',
              )}
              onClick={() => setActiveTab('library')}
            >
              {title}
            </button>
            <button
              type="button"
              className={cn(
                'shrink-0 rounded-8 px-4 py-2 transition-colors sm:px-3',
                activeTab === 'custom' ? 'border border-system-white16 bg-system-white08' : 'bg-system-white04',
              )}
              onClick={() => setActiveTab('custom')}
            >
              {t('tools.textToSpeech.voiceDialog.myVoiceTone')}
            </button>
          </DialogTitle>
        }
        footer={
          <>
            {onUploadAudio ? (
              <div className="mr-auto flex items-center sm:basis-full">
                <Button
                  className="h-auto gap-1 bg-system-white08 px-4 py-2 font-normal text-sm text-system-white leading-[1.4] hover:bg-system-white24 sm:px-2"
                  onClick={() => {
                    onOpenChange(false);
                    onUploadAudio();
                  }}
                >
                  <BaseLinePlus className="size-4 shrink-0" />
                  {t('tools.textToSpeech.voiceDialog.uploadAudio')}
                </Button>
              </div>
            ) : null}
            <Button
              className="h-auto bg-system-white08 px-4 py-2 font-normal text-sm text-system-white leading-[1.4] hover:bg-system-white24"
              onClick={() => onOpenChange(false)}
            >
              {t('common.dialog.cancel')}
            </Button>
            <Button
              className="h-auto bg-system-white px-10 py-2 font-medium text-sm text-system-black leading-[1.4]"
              onClick={handleConfirm}
              disabled={!draftVoiceId && !(allowNoVoice && draftVoiceId === ELEMENT_NO_VOICE_ID)}
            >
              {t('common.dialog.confirm')}
            </Button>
          </>
        }
        trigger={
          triggerFromProps ?? (
            <AudioPreviewCard
              audio={voiceTriggerAudio}
              blurHash={selectedAudio?.blurHash}
              interactive
              action={<BaseLineSwap className="size-5" />}
            />
          )
        }
      >
        <Tabs
          className={cn('min-h-0 flex-1 sm:flex-col', activeTab === 'library' ? 'flex' : 'hidden')}
          orientation="vertical"
          value={language}
          onValueChange={(value) => setLanguage(value as TTSVoiceLanguage)}
        >
          <TabsList className="flex h-full w-[160px] shrink-0 flex-col items-stretch justify-start gap-2 overflow-y-auto border-system-white08 border-r bg-transparent p-4 sm:h-auto sm:w-full sm:flex-row sm:items-center sm:gap-2 sm:overflow-x-auto sm:overflow-y-hidden sm:border-r-0 sm:border-b sm:p-3">
            {languageList.map((item) => (
              <TabsTrigger
                key={item.id}
                value={item.id}
                className="block rounded-8 px-4 py-2 text-left font-normal text-base text-system-white leading-[1.4] hover:bg-system-white04 data-[state=active]:bg-system-white12 sm:shrink-0 sm:px-3 sm:text-sm"
              >
                {item.name}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="min-h-0 flex-1">
              {filteredVoiceList.length ? (
                <div className="hide-scrollbar grid max-h-full grid-cols-2 gap-x-6 gap-y-3 overflow-y-auto p-4 sm:grid-cols-1 sm:gap-y-2 sm:p-3">
                  {filteredVoiceList.map((audio) => (
                    <VoiceOptionItem
                      key={audio.id}
                      audio={audio}
                      selected={audio.id === draftVoiceId}
                      onSelect={setDraftVoiceId}
                      variant="figma"
                    />
                  ))}
                </div>
              ) : (
                <SearchEmpty
                  emptySrc={SEARCH_EMPTY_ICON.selector}
                  textClassName="text-xs"
                  emptyText={t('agentProject.noRecord')}
                />
              )}
            </div>
            {allowNoVoice && noSoundLabel ? (
              <div className="mx-4 shrink-0 border-system-white08 border-t pt-4 pb-4 sm:mx-3 sm:py-3">
                <div
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-3 rounded-12 border border-system-white12 px-3 py-3 text-left text-sm text-system-white transition-colors hover:bg-system-white04',
                    draftVoiceId === ELEMENT_NO_VOICE_ID && 'border-system-white08 bg-system-white08',
                  )}
                  onClick={() => setDraftVoiceId(ELEMENT_NO_VOICE_ID)}
                >
                  <CheckboxFill
                    checked={draftVoiceId === ELEMENT_NO_VOICE_ID}
                    className="text-base"
                    onCheckedChange={() => setDraftVoiceId(ELEMENT_NO_VOICE_ID)}
                  />
                  <span>{noSoundLabel}</span>
                </div>
              </div>
            ) : null}
          </div>
        </Tabs>
        <div className={cn('min-h-0 flex-1', activeTab === 'custom' ? 'block' : 'hidden')}>
          {customVoiceDrafts.length ? (
            <div
              className="hide-scrollbar grid max-h-full grid-cols-2 gap-x-6 gap-y-3 overflow-y-auto p-4 sm:grid-cols-1 sm:gap-y-2 sm:p-3"
              onScroll={handleCustomVoicesScroll}
            >
              {customVoiceDrafts.map((audio) => (
                <VoiceOptionItem
                  key={audio.id}
                  audio={audio as TTSVoiceOption}
                  selected={audio.id === draftVoiceId}
                  onSelect={setDraftVoiceId}
                  variant="figma"
                  onEdit={handleEditCustomVoice}
                />
              ))}
              {isFetchingCustomVoices ? (
                <div className="col-span-2 flex justify-center py-2">
                  <Loading className="size-5 animate-spin text-system-white/72" aria-hidden />
                </div>
              ) : null}
            </div>
          ) : isFetchingCustomVoices ? (
            <div className="flex h-full items-center justify-center">
              <Loading className="size-6 animate-spin text-system-white/72" aria-hidden />
            </div>
          ) : (
            <SearchEmpty
              emptySrc={SEARCH_EMPTY_ICON.selector}
              textClassName="text-xs"
              emptyText={t('tools.textToSpeech.voiceDialog.emptyCustomVoices')}
            />
          )}
        </div>
      </AudioSelectionDialog>

      <VoiceInfoEditDialog
        open={!!editingVoiceId}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setEditingVoiceId(undefined);
          }
        }}
        name={editingName}
        description={editingDescription}
        onNameChange={setEditingName}
        onDescriptionChange={setEditingDescription}
        onCancel={() => setEditingVoiceId(undefined)}
        onConfirm={handleSaveCustomVoice}
        isSaving={isSavingVoiceInfo}
        fieldIdSuffix={voiceInfoEditFieldSuffix}
      />
    </>
  );
};
